import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { formatApproximateMixedFractionLatex as formatMixedFractionLatex } from "../../../../utils/fractionLatex";

/* ========= Utils ========= */
const wrap = (n) => (n < 0 ? `(${n})` : `${n}`);

/* ========= Generator ========= */
export function generateFindATask() {
  const ax = Math.floor(Math.random() * 21) - 10;
  const ay = Math.floor(Math.random() * 21) - 10;
  const bx = Math.floor(Math.random() * 21) - 10;
  const by = Math.floor(Math.random() * 21) - 10;

  const midpoint = [(ax + bx) / 2, (ay + by) / 2];

  return {
    type: "FindATask",
    data: { a: [ax, ay], b: [bx, by], midpoint },
    answer: [ax, ay]
  };
}

export function generateTask() {
  return generateFindATask();
}

/* ========= Treść ========= */
export function renderTask(task) {
  const { b, midpoint } = task.data;

  return (
    <p>
      W odcinku <InlineMath math={'\\overline{AB}'} /> dany jest punkt{" "}
      <InlineMath math={`B = \\left(${formatMixedFractionLatex(b[0])},\\ ${formatMixedFractionLatex(b[1])}\\right)`} />{" "}
      oraz środek odcinka{" "}
      <InlineMath math={`S = \\left(${formatMixedFractionLatex(midpoint[0])},\\ ${formatMixedFractionLatex(midpoint[1])}\\right)`} />.
      Wyznacz współrzędne punktu <InlineMath math={'A'} />.
    </p>
  );
}

/* ========= Input (2× FractionInput) ========= */
export function renderInput(_, userInput, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math={'x_A ='} /></span>
        <FractionInput
          value={userInput?.x || ""}
          onChange={(val) => onChange({ ...userInput, x: val })}
          disabled={disabled}
          label={"x_A"}
        />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math={'y_A ='} /></span>
        <FractionInput
          value={userInput?.y || ""}
          onChange={(val) => onChange({ ...userInput, y: val })}
          disabled={disabled}
          label={"y_A"}
        />
      </div>
    </div>
  );
}

/* ========= Checking (1 pkt za całość) ========= */
export function checkAnswer(task, userInput) {
  const x = userInput?.x?.parsed;
  const y = userInput?.y?.parsed;

  if ([x, y].some(v => v == null || isNaN(v))) return false;

  const [ax, ay] = task.answer;
  return Math.abs(x - ax) < 0.01 && Math.abs(y - ay) < 0.01;
}

/* ========= Odpowiedź ========= */
export function renderAnswer(task) {
  const [ax, ay] = task.answer;
  return <InlineMath math={`A = \\left(${formatMixedFractionLatex(ax)},\\ ${formatMixedFractionLatex(ay)}\\right)`} />;
}

/* ========= Wyjaśnienie ========= */
export function renderExplanation({ data, answer }) {
  if (!data || !answer) return <div style={{ color: "red" }}>Brak danych do wyjaśnienia.</div>;

  const [bx, by] = data.b;
  const [sx, sy] = data.midpoint;
  const [ax, ay] = answer;

  return (
    <div>
      <p><strong>Wyjaśnienie krok po kroku:</strong></p>

      <div style={{ marginBottom: 16 }}>
        <p><strong>Krok 1.</strong> Korzystamy ze wzoru na środek odcinka:</p>
        <BlockMath math={'S = \\left( \\frac{x_A + x_B}{2},\\ \\frac{y_A + y_B}{2} \\right)'} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <p><strong>Krok 2.</strong> Układamy równania:</p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1rem",
            alignItems: "start",
          }}
        >
          <div>
            <div style={{ textAlign: "center", fontWeight: 600 }}><InlineMath math={'x_A'} /></div>
            <BlockMath math={`\\frac{x_A + ${wrap(bx)}}{2} = ${formatMixedFractionLatex(sx)}`} />
            <BlockMath math={`x_A = ${formatMixedFractionLatex(2 * sx)} ${bx < 0 ? "+" : "-"} ${Math.abs(bx)}`} />
            <BlockMath math={`x_A = ${formatMixedFractionLatex(ax)}`} />
          </div>

          <div>
            <div style={{ textAlign: "center", fontWeight: 600 }}><InlineMath math={'y_A'} /></div>
            <BlockMath math={`\\frac{y_A + ${wrap(by)}}{2} = ${formatMixedFractionLatex(sy)}`} />
            <BlockMath math={`y_A = ${formatMixedFractionLatex(2 * sy)} ${by < 0 ? "+" : "-"} ${Math.abs(by)}`} />
            <BlockMath math={`y_A = ${formatMixedFractionLatex(ay)}`} />
          </div>
        </div>
      </div>

      <p><strong>Odpowiedź:</strong></p>
      <BlockMath math={`A = \\left(${formatMixedFractionLatex(ax)},\\ ${formatMixedFractionLatex(ay)}\\right)`} />
    </div>
  );
}

/* ========= User answer preview ========= */
export function renderUserAnswer(userInput) {
  if (!userInput?.x?.raw || !userInput?.y?.raw) return <i>(brak odpowiedzi)</i>;

  const x = formatLatexPreview(userInput.x.raw, "x_A");
  const y = formatLatexPreview(userInput.y.raw, "y_A");

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <div><InlineMath math={x} /></div>
      <div style={{ marginTop: "0.5rem" }}><InlineMath math={y} /></div>
    </div>
  );
}
